import { createElement, type CSSProperties, type ReactElement } from "react";

type CoverageTone = "success" | "warning" | "danger";

interface ApiCoverageDomain {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly upstreamOperations: number;
  readonly lensOperations: number;
  readonly covered: readonly string[];
  readonly gaps: readonly string[];
}

const API_COVERAGE_DOMAINS: readonly ApiCoverageDomain[] = [
  {
    id: "zzk-sec",
    label: "zzk-sec",
    description:
      "Control-plane de identidade, devices, tenants, workspaces, auth e BI operacional.",
    upstreamOperations: 49,
    lensOperations: 8,
    covered: [
      "OTP de operador: send/verify",
      "BI overview, health, timeseries e failure reasons",
    ],
    gaps: [
      "Tenants, devices, instances, enroll tokens e audit log",
      "Workspaces, classification rules, onboarding, API keys e tenant-users",
    ],
  },
  {
    id: "zzk-mem",
    label: "zzk-mem",
    description:
      "Memória operacional por tenant/workspace, transporte MCP, jobs, auditoria e saúde epistêmica.",
    upstreamOperations: 13,
    lensOperations: 1,
    covered: ["Memory health agregado por tenant/workspace"],
    gaps: [
      "MCP tools/resources e gestão de memórias",
      "Jobs, workspaces, audit e políticas de tenant/usuário/permissão",
    ],
  },
];

export function ApiCoveragePanel(): ReactElement {
  return createElement(
    "section",
    {
      className: "zzk-api-coverage-panel",
      "data-testid": "api-coverage-panel",
      "aria-label": "Cobertura operacional das APIs zzk",
    },
    [
      createElement(
        "header",
        { key: "header", className: "zzk-sec-section-header" },
        [
          createElement(
            "div",
            { key: "title" },
            [
              createElement(
                "span",
                { key: "eyebrow", className: "zzk-sec-eyebrow" },
                "Cobertura operacional",
              ),
              createElement(
                "h2",
                { key: "heading", className: "zzk-sec-section-title" },
                "Mapa de APIs zzk-sec e zzk-mem",
              ),
            ],
          ),
          createElement(
            "span",
            { key: "meta", className: "zzk-sec-muted" },
            "Contrato upstream vs. superfície Lens",
          ),
        ],
      ),
      createElement(
        "div",
        { key: "domains", className: "zzk-api-coverage-grid" },
        API_COVERAGE_DOMAINS.map(renderApiCoverageDomain),
      ),
    ],
  );
}

function renderApiCoverageDomain(domain: ApiCoverageDomain): ReactElement {
  const coveragePercent = Math.round(
    (domain.lensOperations / domain.upstreamOperations) * 100,
  );
  const tone = coverageTone(coveragePercent);

  return createElement(
    "article",
    {
      key: domain.id,
      className: "zzk-api-domain-card",
      "data-domain": domain.id,
      "data-tone": tone,
    },
    [
      createElement(
        "header",
        { key: "header", className: "zzk-api-domain-header" },
        [
          createElement(
            "div",
            { key: "title", className: "zzk-api-domain-title" },
            [
              createElement("strong", { key: "label" }, domain.label),
              createElement("span", { key: "desc" }, domain.description),
            ],
          ),
          createElement(
            "span",
            { key: "ratio", className: "zzk-sec-status-pill", "data-tone": tone },
            `${coveragePercent}%`,
          ),
        ],
      ),
      createElement(
        "div",
        {
          key: "meter",
          className: "zzk-api-domain-meter",
          role: "meter",
          "aria-valuemin": 0,
          "aria-valuemax": 100,
          "aria-valuenow": coveragePercent,
          "aria-label": `Cobertura ${domain.label}`,
        },
        createElement("span", {
          style: {
            "--zzk-api-coverage": `${coveragePercent}%`,
          } as CSSProperties,
        }),
      ),
      createElement(
        "div",
        { key: "counts", className: "zzk-api-domain-counts" },
        [
          createElement(
            "span",
            { key: "lens" },
            `${domain.lensOperations} ${operationLabel(
              domain.lensOperations,
            )} no Lens`,
          ),
          createElement(
            "span",
            { key: "upstream" },
            `${domain.upstreamOperations} no upstream`,
          ),
        ],
      ),
      renderCoverageList("Integrado agora", domain.covered),
      renderCoverageList("Sem visibilidade Lens", domain.gaps),
    ],
  );
}

function renderCoverageList(
  title: string,
  items: readonly string[],
): ReactElement {
  return createElement(
    "div",
    { className: "zzk-api-coverage-list" },
    [
      createElement("span", { key: "title" }, title),
      createElement(
        "ul",
        { key: "items" },
        items.map((item) => createElement("li", { key: item }, item)),
      ),
    ],
  );
}

function coverageTone(percent: number): CoverageTone {
  if (percent >= 60) return "success";
  if (percent >= 20) return "warning";
  return "danger";
}

function operationLabel(count: number): string {
  return count === 1 ? "operação" : "operações";
}
